

import React, { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { API_URL } from '../../../utils/constant';
import { fetchSuppliers } from '../../../slices/company/supplierSlice';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import moment from 'moment';
import autoTable from 'jspdf-autotable';
import { customTableStyles } from '../../../utils/tableStylings';

const StockReport = () => {
    const [activeTab, setActiveTab] = useState('vendorPurchases');
    const [showModal, setShowModal] = useState(false);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [modalData, setModalData] = useState(null);
    const [modalPayments, setModalPayments] = useState([]);
    const [allPayments, setAllPayments] = useState([]);

    const today = moment().format("YYYY-MM-DD");
    const firstDayOfMonth = moment().startOf("month").format("YYYY-MM-DD");


    const [stockValueData, setStockValueData] = useState([]);
    const [filters, setFilters] = useState({
        invoice: '',
        party: '',
        article: '',
        month: '',
        segment: '',
        size: '',
        colour: '',
        dateFrom: firstDayOfMonth,
        dateTo: today
    });

    const [pendingFilters, setPendingFilters] = useState({
        invoice: '',
        party: '',
        article: '',
        month: '',
        segment: '',
        size: '',
        colour: '',
        dateFrom: firstDayOfMonth,
        dateTo: today
    });

    const { suppliers = [] } = useSelector((state) => state.suppliers);
    const [segmentOptions, setSegmentOptions] = useState([]);
    const [sizeOptions, setSizeOptions] = useState([]);
    const [colourOptions, setColourOptions] = useState([]);
    const [articleOptions, setArticleOptions] = useState([]);
    const dispatch = useDispatch();
    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const [activeTabOther, setActiveTabOther] = useState(null); // selected size or color
    const [mode, setMode] = useState("size");

    useEffect(() => {
        fetchData();
    }, [filters]);

    useEffect(() => {
        const company = JSON.parse(localStorage.getItem('company'));
        if (company) {
            dispatch(fetchSuppliers(company.company?.id));
        }

        // Fetch all payments
        fetchAllPayments();
    }, []);

    const handlePendingFilterChange = (key, value) => {
        setPendingFilters(prev => ({ ...prev, [key]: value }));
    };

    const applyFilters = () => {
        setFilters(pendingFilters);
    };

    const resetFilters = () => {
        const emptyFilters = {
            invoice: '',
            party: '',
            article: '',
            month: '',
            segment: '',
            size: '',
            colour: '',
            dateFrom: '',
            dateTo: ''
        };
        setPendingFilters(emptyFilters);
        setFilters(emptyFilters);
    };

    const exportToExcel = () => {
        const workbook = XLSX.utils.book_new();

        // Sheet 1: Stock Value (vendorPurchases tab data)
        const stockValueSheet = XLSX.utils.json_to_sheet(data.map((row, index) => ({
            '#': index + 1,
            'Vendor': row.RawProductVendors?.suppliers?.name || 'N/A',
            'Article': row.sku,
            'Segment': getUniqueSegments(row),
            'Sub Segment': getUniqueSubSegments(row),
            'Variant Count': getVariantInfo(row).uniqueCount,
            'Quantity': getVariantInfo(row).totalQty,
            'Invoice': row.invoiceData?.billNumber || 'N/A',
            'Taxable Value': row.invoiceData?.sumOfPrice || 0,
            'Tax': row.invoiceData?.sumOfGst || 0,
            'Total Amount': row.invoiceData?.sumOfTotal || 0,
            'Sold Value': row.soldData?.soldValues || 0,
            'Sold Quantity': row.soldData?.soldQuantity || 0,
            'Sold Variants': row.soldData?.soldVariants || 0,
            'Remaining Stock': row.soldData?.remainingStock || 0,
            'Remaining Variants': row.soldData?.remainingVariants || 0
        })));
        XLSX.utils.book_append_sheet(workbook, stockValueSheet, 'Stock Value');

        // Sheet 2: Vendor Wise Purchases & Payments (stockValueData)
        const vendorPurchasesSheet = XLSX.utils.json_to_sheet(stockValueData.map((row, index) => ({
            '#': index + 1,
            'Vendor': row.vendor,
            'Purchase Value': row.purchaseValue,
            'Purchase Articles': row.purchaseArticles,
            'Purchase Variants': row.purchaseVariants,
            'Quantity': row.quantity,
            'Sales Made': row.salesMade,
            'Remaining Quantity': row.remainingQuantity,
            'Remaining Articles': row.remainingArticles,
            'Remaining Variants': row.remainingVariant
        })));
        XLSX.utils.book_append_sheet(workbook, vendorPurchasesSheet, 'Vendor Purchases');

        // Sheet 3: Vendor Payments
        const paymentsSheet = XLSX.utils.json_to_sheet(allPayments.map((payment, index) => ({
            '#': index + 1,
            'Vendor Name': payment.vendor_name,
            'Payment': payment.payment,
            'Mode of Payment': payment.mode_of_payment,
            'Payment Details': payment.payment_details,
            'Payment Date': payment.payment_date
        })));
        XLSX.utils.book_append_sheet(workbook, paymentsSheet, 'Vendor Payments');

        // Sheet 4: Invoices
        const invoicesSheet = XLSX.utils.json_to_sheet(data.flatMap(row =>
            row.invoiceData?.billData ? row.invoiceData.billData.map((bill, idx) => ({
                '#': idx + 1,
                'Article Number': row.sku,
                'Bill Number': bill.bill_number,
                'Bill Date': bill.bill_date,
                'Supplier': row.RawProductVendors?.suppliers?.name || 'N/A',
                'Taxable Value': row.invoiceData?.sumOfPrice || 0,
                'Tax Amount': row.invoiceData?.sumOfGst || 0,
                'Total Amount': row.invoiceData?.sumOfTotal || 0
            })) : []
        ));
        XLSX.utils.book_append_sheet(workbook, invoicesSheet, 'Invoices');

        XLSX.writeFile(workbook, 'stock_report.xlsx');
    };

    const exportToPDF = () => {
        const doc = new jsPDF('landscape');

        // Title
        doc.setFontSize(16);
        doc.text('Stock Report', 14, 20);

        let yPosition = 30;

        // Stock Value Table
        doc.setFontSize(14);
        doc.text('Stock Value', 14, yPosition);
        yPosition += 10;

        const stockValueTableData = data.map((row, index) => [
            index + 1,
            row.RawProductVendors?.suppliers?.name || 'N/A',
            row.sku,
            getUniqueSegments(row),
            getUniqueSubSegments(row),
            getVariantInfo(row).uniqueCount,
            getVariantInfo(row).totalQty,
            row.invoiceData?.billNumber || 'N/A',
            row.invoiceData?.sumOfPrice || 0,
            row.invoiceData?.sumOfGst || 0,
            row.invoiceData?.sumOfTotal || 0,
            row.soldData?.soldValues || 0,
            row.soldData?.soldQuantity || 0,
            row.soldData?.soldVariants || 0,
            row.soldData?.remainingStock || 0,
            row.soldData?.remainingVariants || 0
        ]);

        autoTable(doc, {
            head: [['#', 'Vendor', 'Article', 'Segment', 'Sub Segment', 'Variants', 'Qty', 'Invoice', 'Taxable', 'Tax', 'Total', 'Sold Val', 'Sold Qty', 'Sold Var', 'Rem Stock', 'Rem Var']],
            body: stockValueTableData,
            startY: yPosition,
            styles: { fontSize: 6, cellPadding: 1 },
            headStyles: { fillColor: [41, 128, 185], textColor: 255, fontSize: 7 },
            margin: { top: 10 }
        });

        // New page for Vendor Purchases
        doc.addPage();
        yPosition = 20;
        doc.setFontSize(14);
        doc.text('Vendor Wise Purchases & Payments', 14, yPosition);
        yPosition += 10;

        const vendorPurchasesTableData = stockValueData.map((row, index) => [
            index + 1,
            row.vendor,
            row.purchaseValue.toFixed(2),
            row.purchaseArticles,
            row.purchaseVariants,
            row.quantity,
            row.salesMade.toFixed(2),
            row.remainingQuantity,
            row.remainingArticles,
            row.remainingVariant
        ]);

        autoTable(doc, {
            head: [['#', 'Vendor', 'Purchase Val', 'Pur Art', 'Pur Var', 'Qty', 'Sales', 'Rem Qty', 'Rem Art', 'Rem Var', 'Rem Val']],
            body: vendorPurchasesTableData,
            startY: yPosition,
            styles: { fontSize: 8, cellPadding: 2 },
            headStyles: { fillColor: [52, 152, 219], textColor: 255, fontSize: 9 },
            margin: { top: 10 }
        });

        // New page for Payments
        doc.addPage();
        yPosition = 20;
        doc.setFontSize(14);
        doc.text('Vendor Payments', 14, yPosition);
        yPosition += 10;

        const paymentsTableData = allPayments.map((payment, index) => [
            index + 1,
            payment.vendor_name,
            payment.payment,
            payment.mode_of_payment,
            payment.payment_details,
            payment.payment_date
        ]);

        autoTable(doc, {
            head: [['#', 'Vendor', 'Payment', 'Mode', 'Details', 'Date']],
            body: paymentsTableData,
            startY: yPosition,
            styles: { fontSize: 8, cellPadding: 2 },
            headStyles: { fillColor: [155, 89, 182], textColor: 255, fontSize: 9 },
            margin: { top: 10 }
        });

        // New page for Invoices
        doc.addPage();
        yPosition = 20;
        doc.setFontSize(14);
        doc.text('Invoices', 14, yPosition);
        yPosition += 10;

        const invoicesTableData = data.flatMap(row =>
            row.invoiceData?.billData ? row.invoiceData.billData.map((bill, idx) => [
                idx + 1,
                row.sku,
                bill.bill_number,
                bill.bill_date,
                row.RawProductVendors?.suppliers?.name || 'N/A',
                row.invoiceData?.sumOfPrice || 0,
                row.invoiceData?.sumOfGst || 0,
                row.invoiceData?.sumOfTotal || 0
            ]) : []
        );

        autoTable(doc, {
            head: [['#', 'Article Number', 'Bill Number', 'Date', 'Supplier', 'Taxable Value', 'Tax', 'Total Amount']],
            body: invoicesTableData,
            startY: yPosition,
            styles: { fontSize: 8, cellPadding: 2 },
            headStyles: { fillColor: [230, 126, 34], textColor: 255, fontSize: 9 },
            margin: { top: 10 }
        });

        doc.save('stock_report.pdf');
    };

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const company = JSON.parse(localStorage.getItem('company'));
            const companyId = company?.company?.id;
            
            const response = await fetch(`${API_URL}/pos/stock-report/${companyId}`);
            const result = await response.json();
            console.log(result);

            // Fetch invoice and sold details for each product
            const dataWithDetails = await Promise.all(result.data.map(async (row) => {
                let invoiceData = null;
                let soldData = null;
                let serialData = [];
                let variantData = [];
                
                try {
                    const invoiceResponse = await fetch(`${API_URL}/pos/purchases-report/${companyId}/${row.id}`);
                    const invoiceResult = await invoiceResponse.json();
                    invoiceData = invoiceResult.data;
                } catch (err) {
                    console.error('Failed to fetch invoice for row', row.id, err);
                }
                
                try {
                    const soldResponse = await fetch(`${API_URL}/pos/sold-report/${companyId}/${row.id}`);
                    const soldResult = await soldResponse.json();
                    soldData = soldResult.data;
                } catch (err) {
                    console.error('Failed to fetch sold data for row', row.id, err);
                }

                // Collect serial data and variant data
                if (row.RawMaterialStocks && row.RawMaterialStocks.length > 0) {
                    row.RawMaterialStocks.forEach(stock => {
                        if (stock.variantStocks && stock.variantStocks.length > 0) {
                            stock.variantStocks.forEach(variant => {
                                if (variant.serial_no) {
                                    serialData.push(variant.serial_no);
                                }
                                if (variant.variant_name) {
                                    variantData.push(variant.variant_name);
                                }
                            });
                        }
                    });
                }
                
                return { ...row, invoiceData, soldData, serialData, variantData };
            }));

            // Filter dataWithDetails based on filters
            let filteredData = dataWithDetails.filter(row => {
                if (filters.party && row.RawProductVendors?.suppliers?.id != filters.party) return false;
                if (filters.article && row.sku !== filters.article) return false;
                if (filters.segment && !getUniqueSegments(row).includes(filters.segment)) return false;
                
                // ✅ Only check dates if product HAS purchase data
                if (row.invoiceData?.billData?.length > 0) {
                    if (filters.dateFrom && !row.invoiceData.billData.some(bill => 
                        new Date(bill.bill_date) >= new Date(filters.dateFrom)
                    )) return false;
                    
                    if (filters.dateTo && !row.invoiceData.billData.some(bill => 
                        new Date(bill.bill_date) <= new Date(filters.dateTo)
                    )) return false;
                }
                // If no invoiceData, date filters don't apply - product stays included
                
                if (filters.month) {
                    const itemMonth = new Date(row.createdAt).getMonth() + 1;
                    if (itemMonth != filters.month) return false;
                }
                return true;
            });

            // ✅ KEY CHANGE: Group ALL products, including those without vendors
            const groupedByVendor = {};
            
            filteredData.forEach(row => {
                // Use 'Unknown Vendor' as fallback for products without vendors
                const vendorId = row.RawProductVendors?.suppliers?.id || 'no-vendor';
                const vendor = row.RawProductVendors?.suppliers?.name || 'Unknown Vendor';
                
                if (!groupedByVendor[vendor]) {
                    groupedByVendor[vendor] = {
                        vendor,
                        vendorId,
                        purchaseValue: 0,
                        purchaseArticles: 0,
                        purchaseArticleData: [],
                        purchaseVariants: 0,
                        purchaseVariantData: {},
                        purchaseSerialData: [],
                        quantity: 0,
                        salesMade: 0,
                        remainingQuantity: 0,
                        remainingArticles: 0,
                        remainingArticleData: [],
                        remainingVariant: 0,
                        remainingVariantData: [],
                        remainingValues: 0,
                        claimValues: 0,
                        claimQuantity: 0,
                        claimVariantsData: []
                    };
                }

                const variantInfo = getVariantInfo(row);
                
                // ✅ Handle null/undefined invoiceData gracefully
                groupedByVendor[vendor].purchaseValue += row.invoiceData?.sumOfTotal || 0;
                groupedByVendor[vendor].purchaseArticles += 1;
                groupedByVendor[vendor].purchaseArticleData.push(row.sku);

                // Collect variant data (handle cases where RawMaterialStocks might be missing)
                if (row.RawMaterialStocks && row.RawMaterialStocks.length > 0) {
                    row.RawMaterialStocks.forEach(stock => {
                        if (stock.variantStocks && stock.variantStocks.length > 0) {
                            stock.variantStocks.forEach(variant => {
                                const key = variant.variant_name || 'Unknown Variant';
                                if (!groupedByVendor[vendor].purchaseVariantData[key]) {
                                    groupedByVendor[vendor].purchaseVariantData[key] = 0;
                                }
                                groupedByVendor[vendor].purchaseVariantData[key] += variant.quantity || 1;
                                if (variant.serial_no) {
                                    groupedByVendor[vendor].purchaseSerialData.push(variant.serial_no);
                                }
                            });
                        }
                    });
                }

                groupedByVendor[vendor].purchaseVariants = Object.keys(groupedByVendor[vendor].purchaseVariantData).length;
                groupedByVendor[vendor].quantity = Object.values(groupedByVendor[vendor].purchaseVariantData).reduce((a, b) => a + b, 0);
                
                // ✅ Handle null/undefined soldData gracefully
                groupedByVendor[vendor].salesMade += row.soldData?.soldValues || 0;
                groupedByVendor[vendor].remainingQuantity += row.soldData?.remainingStock || 0;
                groupedByVendor[vendor].claimQuantity += row.soldData?.claimQuantity || 0;
                groupedByVendor[vendor].remainingArticles += 1;
                groupedByVendor[vendor].remainingArticleData.push(row.sku);
                groupedByVendor[vendor].remainingVariant += row.soldData?.remainingVariants || 0;
                groupedByVendor[vendor].remainingVariantData.push(...(row.soldData?.remainingVariantData || []));
                groupedByVendor[vendor].claimVariantsData.push(...(row.soldData?.claimVariantsData || []));
            });

            const stockValueDataFiltered = Object.values(groupedByVendor).map(row => ({
                ...row,
                purchaseVariantData: Object.entries(row.purchaseVariantData).map(([variant, count]) => ({ variant, count }))
            })).filter(row => {
                // ✅ Allow 'Unknown Vendor' to show when no party filter is applied
                if (filters.party && row.vendorId !== filters.party && row.vendorId !== 'no-vendor') return false;
                return true;
            });

            // Update dynamic options from filtered data
            if (!filters.article && !filters.segment && !filters.size && !filters.colour && !filters.month && !filters.dateFrom && !filters.dateTo) {
                const uniqueSegments = [...new Set(filteredData.map(row => getUniqueSegments(row)).filter(Boolean))];
                const uniqueArticles = [...new Set(filteredData.map(row => row.sku).filter(Boolean))];
                const allSizes = filteredData.flatMap(row => getUniqueSizesArray(row));
                const uniqueSizes = [...new Set(allSizes.filter(Boolean))];
                const allColours = filteredData.flatMap(row => getUniqueColoursArray(row));
                const uniqueColours = [...new Set(allColours.filter(Boolean))];

                setSegmentOptions(uniqueSegments);
                setArticleOptions(uniqueArticles);
                setSizeOptions(uniqueSizes);
                setColourOptions(uniqueColours);
            }

            setData(filteredData);
            setStockValueData(stockValueDataFiltered);
            
        } catch (err) {
            setError('Failed to fetch data');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // const fetchData = async () => {
    //     setLoading(true);
    //     setError(null);
    //     try {

    //         const company = JSON.parse(localStorage.getItem('company'));
    //         const companyId = company?.company?.id;
    //         // Replace with your actual API call
    //         const response = await fetch(`${API_URL}/pos/stock-report/${companyId}`);
    //         const result = await response.json();
    //         console.log(result);
    //         // Fetch invoice and sold details for each product
    //         const dataWithDetails = await Promise.all(result.data.map(async (row) => {
    //             let invoiceData = null;
    //             let soldData = null;
    //             let serialData = [];
    //             let variantData = [];
    //             try {
    //                 const invoiceResponse = await fetch(`${API_URL}/pos/purchases-report/${companyId}/${row.id}`);
    //                 const invoiceResult = await invoiceResponse.json();
    //                 invoiceData = invoiceResult.data;
    //             } catch (err) {
    //                 console.error('Failed to fetch invoice for row', row.id, err);
    //             }
    //             try {
    //                 const soldResponse = await fetch(`${API_URL}/pos/sold-report/${companyId}/${row.id}`);
    //                 const soldResult = await soldResponse.json();
    //                 soldData = soldResult.data;
    //             } catch (err) {
    //                 console.error('Failed to fetch sold data for row', row.id, err);
    //             }
    //             // Collect serial data and variant data
    //             if (row.RawMaterialStocks && row.RawMaterialStocks.length > 0) {
    //                 row.RawMaterialStocks.forEach(stock => {
    //                     if (stock.variantStocks && stock.variantStocks.length > 0) {
    //                         stock.variantStocks.forEach(variant => {
    //                             if (variant.serial_no) {
    //                                 serialData.push(variant.serial_no);
    //                             }
    //                             if (variant.variant_name) {
    //                                 variantData.push(variant.variant_name);
    //                             }
    //                         });
    //                     }
    //                 });
    //             }
    //             return { ...row, invoiceData, soldData, serialData, variantData };
    //         }));

    //         // Filter dataWithDetails based on filters
    //         let filteredData = dataWithDetails.filter(row => {
    //             if (filters.party && row.RawProductVendors?.suppliers?.id != filters.party) return false;
    //             if (filters.article && row.sku !== filters.article) return false;
    //             if (filters.segment && !getUniqueSegments(row).includes(filters.segment)) return false;
    //             // if (filters.size && !getUniqueSizes(row).includes(filters.size)) return false;
    //             // if (filters.colour && !getUniqueColours(row).includes(filters.colour)) return false;
    //             if (filters.dateFrom && (!row.invoiceData?.billData?.some(bill => new Date(bill.bill_date) >= new Date(filters.dateFrom)))) return false;
    //             if (filters.dateTo && (!row.invoiceData?.billData?.some(bill => new Date(bill.bill_date) <= new Date(filters.dateTo)))) return false;
    //             if (filters.month) {
    //                 const itemMonth = new Date(row.createdAt).getMonth() + 1;
    //                 if (itemMonth != filters.month) return false;
    //             }
    //             // Add other filters as needed
    //             return true;
    //         });

    //         const groupedByVendor = {};
    //         filteredData.forEach(row => {
    //             const vendorId = row.RawProductVendors?.suppliers?.id;
    //             const vendor = row.RawProductVendors?.suppliers?.name || 'N/A';
    //             if (!groupedByVendor[vendor]) {
    //                 groupedByVendor[vendor] = {
    //                     vendor,
    //                     vendorId,
    //                     purchaseValue: 0,
    //                     purchaseArticles: 0,
    //                     purchaseArticleData: [],
    //                     purchaseVariants: 0,
    //                     purchaseVariantData: {},
    //                     purchaseSerialData: [],
    //                     quantity: 0,
    //                     salesMade: 0,
    //                     remainingQuantity: 0,
    //                     remainingArticles: 0,
    //                     remainingArticleData: [],
    //                     remainingVariant: 0,
    //                     remainingVariantData: [],
    //                     remainingValues: 0,
    //                     claimValues: 0,
    //                     claimQuantity: 0,
    //                     claimVariantsData: []
    //                 };
    //             }
    //             const variantInfo = getVariantInfo(row);
    //             groupedByVendor[vendor].purchaseValue += row.invoiceData?.sumOfTotal || 0;
    //             groupedByVendor[vendor].purchaseArticles += 1;
    //             groupedByVendor[vendor].purchaseArticleData.push(row.sku);
    //             // Collect variant data
    //             row.RawMaterialStocks.forEach(stock => {
    //                 stock.variantStocks.forEach(variant => {
    //                     const key = variant.variant_name;
    //                     if (!groupedByVendor[vendor].purchaseVariantData[key]) {
    //                         groupedByVendor[vendor].purchaseVariantData[key] = 0;
    //                     }
    //                     groupedByVendor[vendor].purchaseVariantData[key] += variant.quantity || 1;
    //                     if (variant.serial_no) {
    //                         groupedByVendor[vendor].purchaseSerialData.push(variant.serial_no);
    //                     }
    //                 });
    //             });
    //             groupedByVendor[vendor].purchaseVariants = Object.keys(groupedByVendor[vendor].purchaseVariantData).length;
    //             groupedByVendor[vendor].quantity = Object.values(groupedByVendor[vendor].purchaseVariantData).reduce((a, b) => a + b, 0);
    //             groupedByVendor[vendor].salesMade += row.soldData?.soldValues || 0;
    //             groupedByVendor[vendor].remainingQuantity += row.soldData?.remainingStock || 0;
    //             groupedByVendor[vendor].claimQuantity += row.soldData?.claimQuantity || 0;
    //             groupedByVendor[vendor].remainingArticles += 1;
    //             groupedByVendor[vendor].remainingArticleData.push(row.sku);
    //             groupedByVendor[vendor].remainingVariant += row.soldData?.remainingVariants || 0;
    //             groupedByVendor[vendor].remainingVariantData.push(...(row.soldData?.remainingVariantData || []));
    //             groupedByVendor[vendor].claimVariantsData.push(...(row.soldData?.claimVariantsData || []));
    //         });
    //         const stockValueDataFiltered = Object.values(groupedByVendor).map(row => ({
    //             ...row,
    //             purchaseVariantData: Object.entries(row.purchaseVariantData).map(([variant, count]) => ({ variant, count }))
    //         })).filter(row => {
    //             if (filters.party && row.vendorId != filters.party) return false;
    //             // Add other filters for grouped data if needed
    //             return true;
    //         });

    //         const uniqueSegments = [...new Set(filteredData.map(row => getUniqueSegments(row)).filter(Boolean))];
    //         const uniqueArticles = [...new Set(filteredData.map(row => row.sku).filter(Boolean))];
    //         // setSegmentOptions(uniqueSegments);
    //         // setArticleOptions(uniqueArticles);

    //         // Update dynamic options from filtered data only on first load or when supplier filter is selected
    //         if (!filters.article && !filters.segment && !filters.size && !filters.colour && !filters.month && !filters.dateFrom && !filters.dateTo) {
    //             const uniqueSegments = [...new Set(filteredData.map(row => getUniqueSegments(row)).filter(Boolean))];
    //             const uniqueArticles = [...new Set(filteredData.map(row => row.sku).filter(Boolean))];

    //             // Get unique sizes and colours by flattening arrays from all rows
    //             const allSizes = filteredData.flatMap(row => getUniqueSizesArray(row));
    //             const uniqueSizes = [...new Set(allSizes.filter(Boolean))];

    //             const allColours = filteredData.flatMap(row => getUniqueColoursArray(row));
    //             const uniqueColours = [...new Set(allColours.filter(Boolean))];

    //             setSegmentOptions(uniqueSegments);
    //             setArticleOptions(uniqueArticles);
    //             setSizeOptions(uniqueSizes);
    //             setColourOptions(uniqueColours);
    //         }


    //         setData(filteredData);
    //         setStockValueData(stockValueDataFiltered);
    //     } catch (err) {
    //         setError('Failed to fetch data');
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const fetchAllPayments = async () => {
        try {
            const response = await fetch(`${API_URL}/vendor-payments`);
            const payments = await response.json();
            setAllPayments(payments);
        } catch (error) {
            console.error('Error fetching all payments:', error);
        }
    };

    const handleVendorPayments = async (vendorName) => {
        try {
            const response = await fetch(`${API_URL}/vendor-payments`);
            const payments = await response.json();
            const vendorPayments = payments.filter(p => p.vendor_name === vendorName);
            setModalPayments(vendorPayments);
            setModalData({
                type: 'vendorPayments',
                title: `Payments for ${vendorName}`
            });
            setShowModal(true);
        } catch (error) {
            console.error('Error fetching payments:', error);
        }
    };

    const getUniqueSegments = (row) => {
        const segmentsSet = new Set();
        if (row.RawMaterialStocks && row.RawMaterialStocks.length > 0) {
            row.RawMaterialStocks.forEach((stock) => {
                if (stock.variantStocks && stock.variantStocks.length > 0) {
                    stock.variantStocks.forEach((variant) => {
                        if (variant.segment) segmentsSet.add(variant.segment);
                    });
                }
            });
        }
        return Array.from(segmentsSet).join(', ') || 'N/A';
    };

    const getUniqueSizes = (row) => {
        const sizesSet = new Set();
        if (row.RawMaterialStocks && row.RawMaterialStocks.length > 0) {
            row.RawMaterialStocks.forEach((stock) => {
                if (stock.variantStocks && stock.variantStocks.length > 0) {
                    stock.variantStocks.forEach((variant) => {
                        if (variant.variant_name) {
                            const parts = variant.variant_name.split('-');
                            if (parts.length > 0 && parts[0]) sizesSet.add(parts[0]);
                        }
                    });
                }
            });
        }
        return Array.from(sizesSet).join(', ') || 'N/A';
    };

    const getUniqueSizesArray = (row) => {
        const sizesSet = new Set();
        if (row.RawMaterialStocks && row.RawMaterialStocks.length > 0) {
            row.RawMaterialStocks.forEach((stock) => {
                if (stock.variantStocks && stock.variantStocks.length > 0) {
                    stock.variantStocks.forEach((variant) => {
                        if (variant.variant_name) {
                            const parts = variant.variant_name.split('-');
                            if (parts.length > 0 && parts[0]) sizesSet.add(parts[0]);
                        }
                    });
                }
            });
        }
        return Array.from(sizesSet);
    };

    const getUniqueColours = (row) => {
        const coloursSet = new Set();
        if (row.RawMaterialStocks && row.RawMaterialStocks.length > 0) {
            row.RawMaterialStocks.forEach((stock) => {
                if (stock.variantStocks && stock.variantStocks.length > 0) {
                    stock.variantStocks.forEach((variant) => {
                        if (variant.variant_name) {
                            const parts = variant.variant_name.split('-');
                            if (parts.length > 1 && parts[1]) coloursSet.add(parts[1]);
                        }
                    });
                }
            });
        }
        return Array.from(coloursSet).join(', ') || 'N/A';
    };

    const getUniqueColoursArray = (row) => {
        const coloursSet = new Set();
        if (row.RawMaterialStocks && row.RawMaterialStocks.length > 0) {
            row.RawMaterialStocks.forEach((stock) => {
                if (stock.variantStocks && stock.variantStocks.length > 0) {
                    stock.variantStocks.forEach((variant) => {
                        if (variant.variant_name) {
                            const parts = variant.variant_name.split('-');
                            if (parts.length > 1 && parts[1]) coloursSet.add(parts[1]);
                        }
                    });
                }
            });
        }
        return Array.from(coloursSet);
    };

    const getUniqueSubSegments = (row) => {
        const subSegmentsSet = new Set();
        if (row.RawMaterialStocks && row.RawMaterialStocks.length > 0) {
            row.RawMaterialStocks.forEach((stock) => {
                if (stock.variantStocks && stock.variantStocks.length > 0) {
                    stock.variantStocks.forEach((variant) => {
                        if (variant.subSegment) subSegmentsSet.add(variant.subSegment);
                    });
                }
            });
        }
        return Array.from(subSegmentsSet).join(', ') || 'N/A';
    };

    const getVariantInfo = (row) => {
        const variantsSet = new Set();
        let totalQty = 0;
        if (row.RawMaterialStocks && row.RawMaterialStocks.length > 0) {
            row.RawMaterialStocks.forEach((stock) => {
                if (stock.variantStocks && stock.variantStocks.length > 0) {
                    totalQty += stock.variantStocks.length;
                    stock.variantStocks.forEach((variant) => {
                        if (variant.variant_name) variantsSet.add(variant.variant_name);
                    });
                }
            });
        }
        return {
            uniqueCount: variantsSet.size,
            totalQty: totalQty
        };
    };




    const stockValueColumns = [
        {
            name: 'Vendor',
            selector: row => row.vendor,
            sortable: true,
            wrap: true,
            width: "150px"
        },
        {
            name: 'Purchase value',
            selector: row => row.purchaseValue.toFixed(2),
            sortable: true,
            wrap: true,
            width: "150px"
        },
        {
            name: 'Purchase articles',
            cell: row => (
                <button
                    className="btn btn-link p-0 text-decoration-none"
                    onClick={() => {
                        setModalData({
                            type: 'articles',
                            title: 'Purchase Article Details',
                            data: row.purchaseArticleData.map(a => ({ article: a }))
                        });
                        setShowModal(true);
                    }}
                >
                    {row.purchaseArticles}
                </button>
            ),
            sortable: true,
            wrap: true,
            width: "150px"
        },
        {
            name: 'Purchase variants',
            cell: row => (
                <button
                    className="btn btn-link p-0 text-decoration-none"
                    onClick={() => {
                        setModalData({
                            type: 'purchaseVariants',
                            title: 'Purchase Variant Details',
                            data: row.purchaseVariantData
                        });
                        setShowModal(true);
                    }}
                >
                    {row.purchaseVariants}
                </button>
            ),
            sortable: true,
            wrap: true,
            width: "150px"
        },
        {
            name: 'Quantity',
            cell: row => (
                <button
                    className="btn btn-link p-0 text-decoration-none"
                    onClick={() => {
                        setModalData({
                            type: 'purchaseSerials',
                            title: 'Purchase Serial Numbers',
                            data: row.purchaseSerialData
                        });
                        setShowModal(true);
                    }}
                >
                    {row.quantity}
                </button>
            ),
            sortable: true,
            wrap: true,
            width: "150px"
        },
        {
            name: 'Sales Made',
            selector: row => row.salesMade.toFixed(2),
            sortable: true,
            wrap: true,
            width: "150px"
        },
        {
            name: 'Remaining quantity / stock value',
            selector: row => row.remainingQuantity,
            sortable: true,
            wrap: true,
            width: "150px"
        },
        {
            name: 'Remaining articles',
            cell: row => (
                <button
                    className="btn btn-link p-0 text-decoration-none"
                    onClick={() => {
                        setModalData({
                            type: 'remainingArticles',
                            title: 'Remaining Article Details',
                            data: row.remainingArticleData.map(a => ({ article: a }))
                        });
                        setShowModal(true);
                    }}
                >
                    {row.remainingArticles}
                </button>
            ),
            sortable: true,
            wrap: true,
            width: "200px"
        },
        {
            name: 'Remaining variant',
            cell: row => (
                <button
                    className="btn btn-link p-0 text-decoration-none"
                    onClick={() => {
                        setModalData({
                            type: 'remainingVariants',
                            title: 'Remaining Variant Details',
                            data: row.remainingVariantData.map(v => ({ variant: v }))
                        });
                        setShowModal(true);
                    }}
                >
                    {row.remainingVariant}
                </button>
            ),
            sortable: true,
            wrap: true,
            width: "200px"
        },
        {
            name: 'Actions',
            cell: row => (
                <button
                    className="btn btn-primary btn-sm"
                    onClick={() => handleVendorPayments(row.vendor)}
                >
                    View Payments
                </button>
            ),
            wrap: true,
            width: "150px"
        }
    ];


    const buildSizeColorSummary = (variants) => {
        const sizeMap = {};
        const colorMap = {};

        variants.forEach(v => {
            const [size, color] = v.split('-');

            // Size
            if (!sizeMap[size]) sizeMap[size] = [];
            sizeMap[size].push(v);

            // Color
            if (!colorMap[color]) colorMap[color] = [];
            colorMap[color].push(v);
        });

        return {
            sizes: Object.entries(sizeMap).map(([size, data]) => ({
                size,
                data
            })),
            colors: Object.entries(colorMap).map(([color, data]) => ({
                color,
                data
            }))
        };
    };

    const filteredVariations = () => {
        if (!activeTabOther || !modalData.sizes) return [];

        if (mode === "size") {
            const selectedSize = modalData.sizes.find(s => s.size === activeTabOther);
            return selectedSize ? selectedSize.data.map(v => ({
                serial_no: v,
                id: v // you can replace with actual variant id
            })) : [];
        }

        if (mode === "color") {
            const selectedColor = modalData.colors.find(c => c.color === activeTabOther);
            return selectedColor ? selectedColor.data.map(v => ({
                serial_no: v,
                id: v
            })) : [];
        }

        return [];
    };




    const vendorColumns = [
        {
            name: 'Vendor',
            selector: row => {
                const vendorName = row.RawProductVendors?.suppliers?.name;
                return vendorName && vendorName.trim() !== '' ? vendorName : 'Unknown Vendor';
            },
            sortable: true,
            wrap: true,
            width: "150px"
        },
        {
            name: 'Article',
            selector: row => row.sku,
            sortable: true,
            wrap: true,
            width: "150px"
        },
        // {
        //     name: 'Segment',
        //     selector: row => getUniqueSegments(row),
        //     sortable: true,
        //     wrap: true,
        //     width: "150px"
        // },
        // {
        //     name: 'Sub segment',
        //     selector: row => getUniqueSubSegments(row),
        //     sortable: true,
        //     wrap: true,
        //     width: "150px"
        // },
        {
            name: 'Purchased Variant',
            cell: row => (
                <button
                    className="btn btn-link p-0 text-decoration-none"
                    onClick={() => {

                        setModalData({
                            type: 'rowVariants',
                            title: 'Variants',
                            data: row.variantData
                        });
                        setShowModal(true);
                    }}
                >
                    {getVariantInfo(row).uniqueCount}
                </button>
            ),
            sortable: true,
            wrap: true,
            width: "150px"
        },
        {
            name: 'Purchased Quantity',
            cell: row => (
                <button
                    className="btn btn-link p-0 text-decoration-none"
                    onClick={() => {
                        setModalData({
                            type: 'rowSerials',
                            title: 'Serial Numbers',
                            data: row.serialData
                        });
                        setShowModal(true);
                    }}
                >
                    {getVariantInfo(row).totalQty}
                </button>
            ),
            sortable: true,
            wrap: true,
            width: "150px"
        },
        {
            name: 'Invoice',
            cell: row => (
                <button
                    className="btn btn-link p-0 text-decoration-none"
                    onClick={() => {
                        setModalData({
                            type: 'invoices',
                            title: 'Invoice Details',
                            data: row.invoiceData?.billData?.map(bill => ({ invoice: bill.bill_number, date: bill.bill_date })) || []
                        });
                        setShowModal(true);
                    }}
                >
                    {row.invoiceData?.billNumber != 0 ? row.invoiceData?.billNumber : 'N/A'}
                </button>
            ),
            sortable: true,
            wrap: true,
            width: "150px"
        },
        {
            name: 'Taxable value',
            selector: row => row.invoiceData?.sumOfPrice.toFixed(2),
            sortable: true,
            wrap: true,
            width: "150px"
        },
        {
            name: 'Tax',
            selector: row => row.invoiceData?.sumOfGst.toFixed(2),
            sortable: true,
            wrap: true,
            width: "150px"
        },
        {
            name: 'Total amount',
            selector: row => row.invoiceData?.sumOfTotal.toFixed(2),
            sortable: true,
            wrap: true,
            width: "150px"
        },
        {
            name: 'Sold Value',
            selector: row => row.soldData?.soldValues,
            sortable: true,
            wrap: true,
            width: "150px"
        },
        // {
        //     name: 'Sold quantity',
        //     selector: row => row.soldData?.soldQuantity,
        //     sortable: true,
        //     wrap: true,
        //     width: "150px"
        // },
        {
            name: 'Sold quantity',
            cell: row => (

                <button
                    className="btn btn-link p-0 text-decoration-none"
                    onClick={() => {
                        const variants = row.soldData?.soldVariantData || [];

                        const summary = buildSizeColorSummary(variants);

                        // Group unique variants with counts
                        // const grouped = Object.values(
                        //     variants.reduce((acc, v) => {
                        //         if (!acc[v]) {
                        //             acc[v] = { variant: v, count: 0 };
                        //         }
                        //         acc[v].count += 1;
                        //         return acc;
                        //     }, {})
                        // );



                        setModalData({
                            type: 'soldVariants',
                            title: 'Sold Variant Details',
                            ...summary
                        });

                        if (summary.sizes.length) {
                            setMode('size');
                            setActiveTabOther(summary.sizes[0].size);
                        }

                        setShowModal(true);
                    }}
                >
                    {row.soldData?.soldQuantity}
                </button>
            ),
            sortable: true,
            wrap: true,
            width: "150px"
        },

        {
            name: 'Sold variants',
            selector: row => {
                const variants = row.soldData?.soldVariantData || [];

                // Get unique values
                const unique = [...new Set(variants)];

                return unique.length; // return unique count
            },
            sortable: true,
            wrap: true,
            width: "150px"
        },
        {
            name: 'Remaining stock',
            cell: row => (
                <button
                    className="btn btn-link p-0 text-decoration-none"
                    onClick={() => {
                        const variants = row.soldData?.remainingVariantData || [];

                        const summary = buildSizeColorSummary(variants);

                        // Group unique variants with counts
                        // const grouped = Object.values(
                        //     variants.reduce((acc, v) => {
                        //         if (!acc[v]) {
                        //             acc[v] = { variant: v, count: 0 };
                        //         }
                        //         acc[v].count += 1;
                        //         return acc;
                        //     }, {})
                        // );

                        setModalData({
                            type: 'remaining',
                            title: 'Remaining Variant Details',
                            ...summary
                        });
                        if (summary.sizes.length) {
                            setMode('size');
                            setActiveTabOther(summary.sizes[0].size);
                        }

                        setShowModal(true);
                    }}

                >
                    {row.soldData?.remainingStock}
                </button>
            ),
            sortable: true,
            wrap: true,
            width: "150px"
        },
        {
            name: 'Remaining variant',
            selector: row => {
                const variants = row.soldData?.remainingVariantData || [];

                // Get unique values
                const unique = [...new Set(variants)];

                return unique.length; // return unique count
            },
            sortable: true,
            wrap: true,
            width: "200px"
        },
        {
            name: 'Remaining stock',
            cell: row => (
                <button
                    className="btn btn-link p-0 text-decoration-none"
                    onClick={() => {
                        const variants = row.soldData?.remainingVariantData || [];

                        const summary = buildSizeColorSummary(variants);

                        // Group unique variants with counts
                        // const grouped = Object.values(
                        //     variants.reduce((acc, v) => {
                        //         if (!acc[v]) {
                        //             acc[v] = { variant: v, count: 0 };
                        //         }
                        //         acc[v].count += 1;
                        //         return acc;
                        //     }, {})
                        // );

                        setModalData({
                            type: 'remaining',
                            title: 'Remaining Variant Details',
                            ...summary
                        });
                        if (summary.sizes.length) {
                            setMode('size');
                            setActiveTabOther(summary.sizes[0].size);
                        }

                        setShowModal(true);
                    }}

                >
                    {row.soldData?.remainingStock}
                </button>
            ),
            sortable: true,
            wrap: true,
            width: "150px"
        }, {
            name: 'Claim Variants',
            cell: row => (
                <button
                    className="btn btn-link p-0 text-decoration-none"
                    onClick={() => {
                        const variants = row.soldData?.claimVariantsData || [];

                        const summary = buildSizeColorSummary(variants);

                        // Group unique variants with counts
                        // const grouped = Object.values(
                        //     variants.reduce((acc, v) => {
                        //         if (!acc[v]) {
                        //             acc[v] = { variant: v, count: 0 };
                        //         }
                        //         acc[v].count += 1;
                        //         return acc;
                        //     }, {})
                        // );

                        setModalData({
                            type: 'claimed',
                            title: 'Claimed Variant Details',
                            ...summary
                        });
                        if (summary.sizes.length) {
                            setMode('size');
                            setActiveTabOther(summary.sizes[0].size);
                        }

                        setShowModal(true);
                    }}

                >
                    {row.soldData?.claimVariantsData.length}
                </button>
            ),
            sortable: true,
            wrap: true,
            width: "150px"
        },
    ];

    const getUniqueVariantCounts = (variants) => {
        const counts = {};

        variants.forEach(v => {
            counts[v] = (counts[v] || 0) + 1;
        });

        return Object.entries(counts).map(([variant, count]) => ({
            variant,
            count
        }));
    };

    return (
        <div className="card">
            <div className="card-header">
                <h3 className="text-primary mb-0">Stock Report</h3>
            </div>
            <div className='card-body'>
                <h5 className="card-title">Filters</h5>
                <div className="row g-3 mb-3">
                    <div className="col-md-3">
                        <select
                            value={pendingFilters.party}
                            onChange={e => handlePendingFilterChange('party', e.target.value)}
                            className="form-select"
                        >
                            <option value="">Suppliers</option>
                            {suppliers.map(supplier => (
                                <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <select
                            value={pendingFilters.article}
                            onChange={e => handlePendingFilterChange('article', e.target.value)}
                            className="form-select"
                        >
                            <option value="">Article wise</option>
                            {articleOptions.map(article => (
                                <option key={article} value={article}>{article}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <select
                            value={pendingFilters.segment}
                            onChange={e => handlePendingFilterChange('segment', e.target.value)}
                            className="form-select"
                        >
                            <option value="">Segment wise</option>
                            {segmentOptions.map(segment => (
                                <option key={segment} value={segment}>{segment}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <select
                            value={pendingFilters.month}
                            onChange={e => handlePendingFilterChange('month', e.target.value)}
                            className="form-select"
                        >
                            <option value="">Month wise</option>
                            {monthNames.map((name, index) => (
                                <option key={index + 1} value={index + 1}>{name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <input
                            type="date"
                            placeholder="Date from"
                            value={pendingFilters.dateFrom}
                            onChange={e => handlePendingFilterChange('dateFrom', e.target.value)}
                            className="form-control"
                        />
                    </div>
                    <div className="col-md-3">
                        <input
                            type="date"
                            placeholder="Date to"
                            value={pendingFilters.dateTo}
                            onChange={e => handlePendingFilterChange('dateTo', e.target.value)}
                            className="form-control"
                        />
                    </div>
                    {/* <div className="col-md-3">
                            <select
                                value={pendingFilters.size}
                                onChange={e => handlePendingFilterChange('size', e.target.value)}
                                className="form-select"
                            >
                                <option value="">Size wise</option>
                                {sizeOptions.map(size => (
                                    <option key={size} value={size}>{size}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <select
                                value={pendingFilters.colour}
                                onChange={e => handlePendingFilterChange('colour', e.target.value)}
                                className="form-select"
                            >
                                <option value="">Colour wise</option>
                                {colourOptions.map(colour => (
                                    <option key={colour} value={colour}>{colour}</option>
                                ))}
                            </select>
                        </div> */}
                </div>
                <div className="d-flex gap-2 mb-3">
                    <button
                        onClick={applyFilters}
                        className="btn btn-primary"
                    >
                        Apply
                    </button>
                    <button
                        onClick={resetFilters}
                        className="btn btn-secondary"
                    >
                        Reset
                    </button>
                    <button
                        onClick={exportToExcel}
                        className="btn btn-success"
                        disabled={data.length === 0 && stockValueData.length === 0}
                    >
                        Export Excel
                    </button>
                    <button
                        onClick={exportToPDF}
                        className="btn btn-danger"
                        disabled={data.length === 0 && stockValueData.length === 0}
                    >
                        Export PDF
                    </button>
                </div>
                <ul className="nav nav-tabs" id="stockReportTab" role="tablist">

                    <li className="nav-item" role="presentation">
                        <button
                            className={`nav-link ${activeTab === 'vendorPurchases' ? 'active' : ''}`}
                            onClick={() => setActiveTab('vendorPurchases')}
                            type="button"
                        >
                            Stock Value

                        </button>
                    </li>
                    <li className="nav-item" role="presentation">
                        <button
                            className={`nav-link ${activeTab === 'stockValue' ? 'active' : ''}`}
                            onClick={() => setActiveTab('stockValue')}
                            type="button"
                        >
                            Vendor Wise Purchases & Payments
                        </button>
                    </li>
                </ul>
                <div className="tab-content" id="stockReportTabContent">

                    {activeTab == 'vendorPurchases' && (
                        <div className="tab-pane fade show active" id="vendorPurchases" role="tabpanel">
                            <div className="mt-4">
                                {loading ? (
                                    <div className="d-flex justify-content-center">
                                        <div className="spinner-border" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <DataTable
                                        columns={vendorColumns}
                                        data={data}
                                        pagination
                                        highlightOnHover
                                        striped
                                        customStyles={customTableStyles}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                    {activeTab == 'stockValue' && (
                        <div className="tab-pane fade show active" id="stockValue" role="tabpanel">
                            <div className="mt-4">
                                {loading ? (
                                    <div className="d-flex justify-content-center">
                                        <div className="spinner-border" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <DataTable
                                        columns={stockValueColumns}
                                        data={stockValueData}
                                        pagination
                                        highlightOnHover
                                        striped
                                        customStyles={customTableStyles}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal for details */}
            {showModal && modalData && (
                <div className="modal show d-block" tabIndex="-1">
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{modalData.title}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                {modalData.type === 'invoices' && (
                                    <ul className="list-group">
                                        {modalData.data.map((item, idx) => (
                                            <li key={idx} className="list-group-item">
                                                Invoice: {item.invoice} - Date: {moment(item.date).format('DD/MM/YYYY')}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {modalData.type === 'remaining' && (
                                    // <ul className="list-group">
                                    //     {modalData.data.map((item, idx) => (
                                    //         <li key={idx} className="list-group-item">
                                    //             Variant: {item.variant} - Count: {item.count}
                                    //         </li>
                                    //     ))}
                                    // </ul>
                                    <div className="row">

                                        {/* Left Side Options */}
                                        <div className="col-3 border-end">
                                            <div
                                                className={`p-2 mb-2 cursor-pointer ${mode === "size" && "bg-primary text-white"}`}
                                                onClick={() => {
                                                    setMode("size");
                                                    if (modalData.sizes.length) setActiveTabOther(modalData.sizes[0].size);
                                                }}
                                            >
                                                Size
                                            </div>
                                            <div
                                                className={`p-2 cursor-pointer ${mode === "color" && "bg-primary text-white"}`}
                                                onClick={() => {
                                                    setMode("color");
                                                    if (modalData.colors.length) setActiveTabOther(modalData.colors[0].color);
                                                }}
                                            >
                                                Color
                                            </div>
                                        </div>

                                        {/* Right Side */}
                                        <div className="col-9">

                                            {/* Size/Color Tabs */}
                                            <div className="mb-3 d-flex gap-2 flex-wrap">
                                                {mode === "size" && modalData.sizes?.map((s, i) => (
                                                    <button
                                                        key={i}
                                                        className={`btn ${activeTabOther === s.size ? "btn-primary" : "btn-outline-primary"}`}
                                                        onClick={() => setActiveTabOther(s.size)}
                                                    >
                                                        Size: {s.size} (Qty: {s.data.length})
                                                    </button>
                                                ))}

                                                {mode === "color" && modalData.colors?.map((c, i) => (
                                                    <button
                                                        key={i}
                                                        className={`btn ${activeTabOther === c.color ? "btn-primary" : "btn-outline-primary"}`}
                                                        onClick={() => setActiveTabOther(c.color)}
                                                    >
                                                        Color: {c.color} (Qty: {c.data.length})
                                                    </button>
                                                ))}
                                            </div>

                                            {/* Serial Buttons (Size/Color Mode) */}
                                            <div className="row">
                                                {filteredVariations().map((data, index) => {
                                                    return (
                                                        <div className="col-3 mb-3" key={index}>
                                                            <button
                                                                type='button'
                                                                className={`btn w-100 btn-success text-white fs-18`}
                                                            >
                                                                {data.serial_no}
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                        </div>
                                    </div>


                                )}
                                {modalData.type === 'claimed' && (
                                    // <ul className="list-group">
                                    //     {modalData.data.map((item, idx) => (
                                    //         <li key={idx} className="list-group-item">
                                    //             Variant: {item.variant} - Count: {item.count}
                                    //         </li>
                                    //     ))}
                                    // </ul>
                                    <div className="row">

                                        {/* Left Side Options */}
                                        <div className="col-3 border-end">
                                            <div
                                                className={`p-2 mb-2 cursor-pointer ${mode === "size" && "bg-primary text-white"}`}
                                                onClick={() => {
                                                    setMode("size");
                                                    if (modalData.sizes.length) setActiveTabOther(modalData.sizes[0].size);
                                                }}
                                            >
                                                Size
                                            </div>
                                            <div
                                                className={`p-2 cursor-pointer ${mode === "color" && "bg-primary text-white"}`}
                                                onClick={() => {
                                                    setMode("color");
                                                    if (modalData.colors.length) setActiveTabOther(modalData.colors[0].color);
                                                }}
                                            >
                                                Color
                                            </div>
                                        </div>

                                        {/* Right Side */}
                                        <div className="col-9">

                                            {/* Size/Color Tabs */}
                                            <div className="mb-3 d-flex gap-2 flex-wrap">
                                                {mode === "size" && modalData.sizes?.map((s, i) => (
                                                    <button
                                                        key={i}
                                                        className={`btn ${activeTabOther === s.size ? "btn-primary" : "btn-outline-primary"}`}
                                                        onClick={() => setActiveTabOther(s.size)}
                                                    >
                                                        Size: {s.size} (Qty: {s.data.length})
                                                    </button>
                                                ))}

                                                {mode === "color" && modalData.colors?.map((c, i) => (
                                                    <button
                                                        key={i}
                                                        className={`btn ${activeTabOther === c.color ? "btn-primary" : "btn-outline-primary"}`}
                                                        onClick={() => setActiveTabOther(c.color)}
                                                    >
                                                        Color: {c.color} (Qty: {c.data.length})
                                                    </button>
                                                ))}
                                            </div>

                                            {/* Serial Buttons (Size/Color Mode) */}
                                            <div className="row">
                                                {filteredVariations().map((data, index) => {
                                                    return (
                                                        <div className="col-3 mb-3" key={index}>
                                                            <button
                                                                type='button'
                                                                className={`btn w-100 btn-success text-white fs-18`}
                                                            >
                                                                {data.serial_no}
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                        </div>
                                    </div>


                                )}

                                {modalData.type === 'articles' && (
                                    <ul className="list-group">
                                        {modalData.data.map((item, idx) => (
                                            <li key={idx} className="list-group-item">
                                                Article: {item.article}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {modalData.type === 'remainingArticles' && (
                                    <ul className="list-group">
                                        {modalData.data.map((item, idx) => (
                                            <li key={idx} className="list-group-item">
                                                Article: {item.article}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {modalData.type === 'remainingVariants' && (
                                    <ul className="list-group">
                                        {modalData.data.map((item, idx) => (
                                            <li key={idx} className="list-group-item">
                                                Variant: {item.variant}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {modalData.type === 'purchaseVariants' && (
                                    <ul className="list-group">
                                        {modalData.data.map((item, idx) => (
                                            <li key={idx} className="list-group-item">
                                                Variant: {item.variant} - Serial No Count: {item.count}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {modalData.type === 'purchaseSerials' && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                        {modalData.data.map((serial, idx) => (
                                            <span key={idx} style={{ backgroundColor: '#f8f9fa', padding: '5px 10px', borderRadius: '4px', border: '1px solid #dee2e6' }}>
                                                {serial}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                {modalData.type === 'rowSerials' && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                        {modalData.data.map((serial, idx) => (
                                            <span key={idx} style={{ backgroundColor: '#f8f9fa', padding: '5px 10px', borderRadius: '4px', border: '1px solid #dee2e6' }}>
                                                {serial}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                {modalData.type === 'rowVariants' && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                        {getUniqueVariantCounts(modalData.data).map((item, idx) => (
                                            <span
                                                key={idx}
                                                style={{
                                                    backgroundColor: '#f8f9fa',
                                                    padding: '5px 10px',
                                                    borderRadius: '4px',
                                                    border: '1px solid #dee2e6'
                                                }}
                                            >
                                                {item.variant} (Qty: {item.count})
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {modalData.type === 'soldVariants' && (
                                    <div className="row">

                                        {/* Left Side Options */}
                                        <div className="col-3 border-end">
                                            <div
                                                className={`p-2 mb-2 cursor-pointer ${mode === "size" && "bg-primary text-white"}`}
                                                onClick={() => {
                                                    setMode("size");
                                                    if (modalData.sizes.length) setActiveTabOther(modalData.sizes[0].size);
                                                }}
                                            >
                                                Size
                                            </div>
                                            <div
                                                className={`p-2 cursor-pointer ${mode === "color" && "bg-primary text-white"}`}
                                                onClick={() => {
                                                    setMode("color");
                                                    if (modalData.colors.length) setActiveTabOther(modalData.colors[0].color);
                                                }}
                                            >
                                                Color
                                            </div>
                                        </div>

                                        {/* Right Side */}
                                        <div className="col-9">

                                            {/* Size/Color Tabs */}
                                            <div className="mb-3 d-flex gap-2 flex-wrap">
                                                {mode === "size" && modalData.sizes?.map((s, i) => (
                                                    <button
                                                        key={i}
                                                        className={`btn ${activeTabOther === s.size ? "btn-primary" : "btn-outline-primary"}`}
                                                        onClick={() => setActiveTabOther(s.size)}
                                                    >
                                                        Size: {s.size} (Qty: {s.data.length})
                                                    </button>
                                                ))}

                                                {mode === "color" && modalData.colors?.map((c, i) => (
                                                    <button
                                                        key={i}
                                                        className={`btn ${activeTabOther === c.color ? "btn-primary" : "btn-outline-primary"}`}
                                                        onClick={() => setActiveTabOther(c.color)}
                                                    >
                                                        Color: {c.color} (Qty: {c.data.length})
                                                    </button>
                                                ))}
                                            </div>

                                            {/* Serial Buttons (Size/Color Mode) */}
                                            <div className="row">
                                                {filteredVariations().map((data, index) => {
                                                    return (
                                                        <div className="col-3 mb-3" key={index}>
                                                            <button
                                                                type='button'
                                                                className={`btn w-100 btn-success text-white fs-18`}
                                                            >
                                                                {data.serial_no}
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                        </div>
                                    </div>
                                )}

                                {modalData.type === 'vendorPayments' && (
                                    <div>
                                        {modalPayments.length > 0 ? (
                                            <table className="table table-striped">
                                                <thead>
                                                    <tr>
                                                        <th>Payment Made</th>
                                                        <th>Mode of Payment</th>
                                                        <th>Payment Details</th>
                                                        <th>Payment Date</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {modalPayments.map((payment, idx) => (
                                                        <tr key={idx}>
                                                            <td>{payment.payment}</td>
                                                            <td>{payment.mode_of_payment}</td>
                                                            <td>{payment.payment_details}</td>
                                                            <td>{new Date(payment.payment_date).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        ) : (
                                            <p>No payments found for this vendor.</p>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showModal && <div className="modal-backdrop show"></div>}
        </div>
    );
};

export default StockReport;
